import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import redditApi from '../../../services/redditApi';
import RhDatePicker from './RhDatePicker';
import ArchiveToggle from '../components/ArchiveToggle';

/* ─── Inline Select (mirrors AccountPerformanceTab pattern) ─────────────── */

function Select({ value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const selected = options.find((o) => o.value === value);

  function openMenu() {
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = Math.max(rect.width, 200);
    const spaceBelow = window.innerHeight - rect.bottom;
    const menuHeight = Math.min(options.length * 40 + 8, 260);
    const openUpward = spaceBelow < menuHeight && rect.top > menuHeight;
    const wouldOverflowRight = rect.left + menuWidth > window.innerWidth - 12;
    const horizontalStyle = wouldOverflowRight
      ? { right: window.innerWidth - rect.right, left: 'auto' }
      : { left: rect.left };
    setMenuStyle({
      position: 'fixed',
      width: menuWidth,
      zIndex: 9999,
      ...horizontalStyle,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
    });
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    }
    function handleScroll() { setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  return (
    <div className={`rh-custom-select${open ? ' rh-custom-select--open' : ''}`}>
      <button
        type="button"
        ref={triggerRef}
        className="rh-custom-select-trigger"
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        <span>{selected ? selected.label : placeholder}</span>
        <svg className="rh-custom-select-chevron" width="12" height="12" fill="none"
          stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && createPortal(
        <div className="rh-custom-select-menu" ref={menuRef} style={menuStyle}>
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`rh-custom-select-option${value === o.value ? ' selected' : ''}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {value === o.value && (
                <svg width="12" height="12" fill="none" stroke="currentColor"
                  strokeWidth="2.5" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
              {o.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ─── Sortable column header (horizontal) ───────────────────────────────── */

function SortTh({ col, label, sort, onSort, style, title }) {
  const active = sort.col === col;
  return (
    <th
      title={title}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
      onClick={() => onSort(col)}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {label}
        <svg
          width="10" height="10" fill="none" stroke="currentColor"
          strokeWidth="2.5" viewBox="0 0 24 24"
          style={{ opacity: active ? 1 : 0.3, transition: 'opacity 0.15s' }}
        >
          {active && sort.dir === 'asc'
            ? <polyline points="18 15 12 9 6 15" />
            : <polyline points="6 9 12 15 18 9" />
          }
        </svg>
      </span>
    </th>
  );
}

/* ─── Sortable column header (vertical, narrow) ─────────────────────────── */

function VerticalSortTh({ col, label, sort, onSort, title }) {
  const active = sort.col === col;
  return (
    <th
      title={title || label}
      onClick={() => onSort(col)}
      style={{
        cursor: 'pointer', userSelect: 'none',
        width: '42px', minWidth: '42px', maxWidth: '42px',
        padding: '8px 4px', verticalAlign: 'bottom', textAlign: 'center',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
        <span style={{
          writingMode: 'vertical-lr',
          transform: 'rotate(180deg)',
          fontSize: '0.72rem',
          fontWeight: 600,
          letterSpacing: '0.04em',
          lineHeight: 1,
          color: active ? 'var(--rh-orange, #e55)' : 'inherit',
        }}>
          {label}
        </span>
        <svg
          width="9" height="9" fill="none" stroke="currentColor"
          strokeWidth="2.5" viewBox="0 0 24 24"
          style={{ opacity: active ? 1 : 0.25, transition: 'opacity 0.15s', flexShrink: 0 }}
        >
          {active && sort.dir === 'asc'
            ? <polyline points="18 15 12 9 6 15" />
            : <polyline points="6 9 12 15 18 9" />
          }
        </svg>
      </div>
    </th>
  );
}

/* ─── Deletion rate badge ────────────────────────────────────────────────── */

function RateCell({ rate }) {
  const cls =
    rate >= 50 ? 'rh-badge-deleted' :
    rate >= 25 ? 'rh-badge-overdue' :
    rate > 0   ? 'rh-badge-dws'     :
                 'rh-badge-live';
  return (
    <span className={`rh-badge ${cls}`} style={{ minWidth: '36px', textAlign: 'center', fontSize: '0.72rem' }}>
      {rate}%
    </span>
  );
}

/* ─── Device type options ────────────────────────────────────────────────── */

const DEVICE_OPTIONS = [
  { value: '', label: 'All Devices' },
  { value: 'mobile',  label: 'Mobile' },
  { value: 'tablet',  label: 'Tablet' },
  { value: 'desktop', label: 'Desktop' },
  { value: 'unknown', label: 'Unknown' },
];

const BROWSER_OPTIONS = [
  { value: '', label: 'All Browsers' },
  { value: 'Chrome',  label: 'Chrome' },
  { value: 'Safari',  label: 'Safari' },
  { value: 'Firefox', label: 'Firefox' },
  { value: 'Edge',    label: 'Edge' },
  { value: 'Opera',   label: 'Opera' },
  { value: 'unknown', label: 'Unknown' },
];

const OS_OPTIONS = [
  { value: '', label: 'All OS' },
  { value: 'iOS',     label: 'iOS' },
  { value: 'Android', label: 'Android' },
  { value: 'macOS',   label: 'macOS' },
  { value: 'Windows', label: 'Windows' },
  { value: 'Linux',   label: 'Linux' },
  { value: 'unknown', label: 'Unknown' },
];

/* ─── Main component ─────────────────────────────────────────────────────── */

export default function UserAgentAnalysisTab() {
  const [results, setResults]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  // Filters
  const [redditors, setRedditors] = useState([]);
  const [redditorId, setRedditorId] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [browserName, setBrowserName] = useState('');
  const [osName, setOsName]       = useState('');
  const [dateFrom, setDateFrom]   = useState('');
  const [dateTo, setDateTo]       = useState('');
  const [showDeactivated, setShowDeactivated] = useState(false);

  // Sorting — default: redditor_name asc, then deletion_rate desc per bucket
  const [sort, setSort] = useState({ col: 'redditor_name', dir: 'asc' });

  /* ── Fetch redditor list for filter dropdown ── */
  useEffect(() => {
    redditApi.get('/admin/redditors/')
      .then(({ data }) => setRedditors(Array.isArray(data) ? data : (data.results || [])))
      .catch(() => {});
  }, []);

  /* ── Fetch UA analysis data ── */
  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (redditorId)  params.set('redditor_id',  redditorId);
    if (deviceType)  params.set('device_type',  deviceType);
    if (browserName) params.set('browser_name', browserName);
    if (osName)      params.set('os_name',       osName);
    if (dateFrom)    params.set('date_from',     dateFrom);
    if (dateTo)      params.set('date_to',       dateTo);
    if (showDeactivated) params.set('include_deactivated', 'true');
    const qs = params.toString();
    redditApi.get(`/admin/ua-analysis/${qs ? `?${qs}` : ''}`)
      .then(({ data }) => setResults(data.results || []))
      .catch(() => setError('Failed to load user agent analysis data.'))
      .finally(() => setLoading(false));
  }, [redditorId, deviceType, browserName, osName, dateFrom, dateTo, showDeactivated]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── Sorting ── */
  function handleSort(col) {
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'desc' }
    );
  }

  const sorted = [...results].sort((a, b) => {
    let av = a[sort.col], bv = b[sort.col];
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av == null) av = '';
    if (bv == null) bv = '';
    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
    if (av > bv) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  /* ── Redditor options ── */
  const redditorOptions = [
    { value: '', label: 'All Redditors' },
    ...redditors
      .filter((r) => !r.is_admin && !r.is_accountant)
      .map((r) => ({ value: r.id, label: r.full_name })),
  ];

  /* ── Summary KPIs ── */
  const totalBuckets   = results.length;
  const totalSubmitted = results.reduce((s, r) => s + r.total, 0);
  const totalDeleted   = results.reduce((s, r) => s + r.deleted + r.instant_deleted, 0);
  const overallRate    = totalSubmitted > 0
    ? Math.round(totalDeleted / totalSubmitted * 1000) / 10
    : 0;

  const hasFilters = redditorId || deviceType || browserName || osName || dateFrom || dateTo || showDeactivated;
  const sortProps  = { sort, onSort: handleSort };

  const COL_COUNT = 11;

  return (
    <div className="rh-tab-inner">

      {/* Header */}
      <div className="rh-section-header" style={{ marginBottom: '16px' }}>
        <h2 className="rh-section-title" style={{ margin: 0 }}>User Agent Analysis</h2>
        <p className="rh-section-subtitle" style={{ margin: '4px 0 0', color: 'var(--rh-text-muted)', fontSize: '0.85rem' }}>
          Deletion rates by Redditor and device / browser / OS — based on the browser used when submitting via this dashboard
        </p>
      </div>

      {/* KPI summary row */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {[
          { label: 'Buckets', value: totalBuckets },
          { label: 'Total Submissions', value: totalSubmitted },
          { label: 'Total Deleted', value: totalDeleted },
          { label: 'Overall Deletion Rate', value: `${overallRate}%` },
        ].map(({ label, value }) => (
          <div key={label} className="rh-card"
            style={{ flex: '1 1 140px', padding: '12px 16px', minWidth: '120px' }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
              {label}
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, lineHeight: 1 }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="rh-card" style={{ padding: '12px 16px', marginBottom: '16px' }}>
        <div className="rh-filter-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>

          <div>
            <label className="rh-filter-label" style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: 'var(--rh-text-muted)' }}>
              Redditor
            </label>
            <Select value={redditorId} onChange={setRedditorId} options={redditorOptions} placeholder="All Redditors" />
          </div>

          <div>
            <label className="rh-filter-label" style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: 'var(--rh-text-muted)' }}>
              Device
            </label>
            <Select value={deviceType} onChange={setDeviceType} options={DEVICE_OPTIONS} placeholder="All Devices" />
          </div>

          <div>
            <label className="rh-filter-label" style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: 'var(--rh-text-muted)' }}>
              Browser
            </label>
            <Select value={browserName} onChange={setBrowserName} options={BROWSER_OPTIONS} placeholder="All Browsers" />
          </div>

          <div>
            <label className="rh-filter-label" style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: 'var(--rh-text-muted)' }}>
              OS
            </label>
            <Select value={osName} onChange={setOsName} options={OS_OPTIONS} placeholder="All OS" />
          </div>

          <div>
            <label className="rh-filter-label" style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: 'var(--rh-text-muted)' }}>
              Submitted from
            </label>
            <RhDatePicker value={dateFrom} onChange={setDateFrom} placeholder="From date" />
          </div>

          <div>
            <label className="rh-filter-label" style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: 'var(--rh-text-muted)' }}>
              Submitted to
            </label>
            <RhDatePicker value={dateTo} onChange={setDateTo} placeholder="To date" />
          </div>

          <div style={{ alignSelf: 'flex-end' }}>
            <ArchiveToggle
              checked={showDeactivated}
              onChange={setShowDeactivated}
              onLabel="Showing deactivated"
              offLabel="Show deactivated"
              title="Deactivated redditors are hidden by default"
            />
          </div>

          {hasFilters && (
            <button
              type="button"
              className="rh-btn-secondary"
              style={{ alignSelf: 'flex-end' }}
              onClick={() => {
                setRedditorId('');
                setDeviceType('');
                setBrowserName('');
                setOsName('');
                setDateFrom('');
                setDateTo('');
                setShowDeactivated(false);
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rh-card" style={{ padding: 0, overflow: 'hidden' }}>
        {error && (
          <div style={{ padding: '16px', color: 'var(--rh-danger)' }}>{error}</div>
        )}

        {!error && (
          <div className="rh-table-wrap rh-table-scroll">
            <table className="rh-table">
              <colgroup>
                <col style={{ minWidth: '130px' }} />
                <col style={{ minWidth: '200px' }} />
                <col style={{ minWidth: '80px' }} />
                <col style={{ minWidth: '80px' }} />
                <col style={{ minWidth: '80px' }} />
                <col style={{ width: '42px' }} />
                <col style={{ width: '42px' }} />
                <col style={{ width: '42px' }} />
                <col style={{ width: '42px' }} />
                <col style={{ width: '52px' }} />
                <col style={{ width: '52px' }} />
                <col style={{ minWidth: '90px' }} />
              </colgroup>
              <thead>
                <tr>
                  <SortTh col="redditor_name"         label="Redditor"    {...sortProps} />
                  <SortTh col="ua_bucket"             label="UA Bucket"   title="Device / Browser / OS combination" {...sortProps} />
                  <SortTh col="device_type"           label="Device"      {...sortProps} />
                  <SortTh col="browser_name"          label="Browser"     {...sortProps} />
                  <SortTh col="os_name"               label="OS"          {...sortProps} />
                  <VerticalSortTh col="total"         label="Total"       title="Submissions in this bucket (submitted + live + deleted + instant deleted)" {...sortProps} />
                  <VerticalSortTh col="live"          label="Live"        title="Currently live or pending review (Submitted + Live statuses)" {...sortProps} />
                  <VerticalSortTh col="deleted"       label="Deleted"     title="Comments confirmed deleted by Reddit" {...sortProps} />
                  <VerticalSortTh col="instant_deleted" label="Inst. Del." title="Deleted While Submitting (DWS — deleted immediately by Reddit)" {...sortProps} />
                  <VerticalSortTh col="deletion_rate"          label="Del. Rate"    title="(Deleted + Instant Deleted) ÷ Total × 100" {...sortProps} />
                  <VerticalSortTh col="instant_deletion_rate"  label="Inst. Rate"   title="Instant Deleted ÷ Total × 100" {...sortProps} />
                  <SortTh col="last_submission_at"    label="Last Submit" title="Most recent submission date in this bucket" {...sortProps} />
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={COL_COUNT} className="rh-empty">Loading…</td>
                  </tr>
                )}
                {!loading && sorted.length === 0 && (
                  <tr>
                    <td colSpan={COL_COUNT} className="rh-empty">
                      No submissions found for the selected filters.
                    </td>
                  </tr>
                )}
                {!loading && sorted.map((row, idx) => {
                  const isLowSample = row.total < 5;
                  return (
                    <tr
                      key={`${row.redditor_id}-${row.ua_bucket}-${idx}`}
                      className={isLowSample ? 'row-overdue' : ''}
                      title={isLowSample ? `Low sample size (${row.total} submission${row.total !== 1 ? 's' : ''}) — deletion rate may not be representative` : undefined}
                    >
                      <td style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{row.redditor_name}</td>

                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                        <span className="rh-badge rh-badge-submitted" style={{ fontSize: '0.72rem' }}>
                          {row.ua_bucket}
                        </span>
                      </td>

                      <td style={{ whiteSpace: 'nowrap' }}>
                        <span className={`rh-badge ${
                          row.device_type === 'mobile'  ? 'rh-badge-live' :
                          row.device_type === 'tablet'  ? 'rh-badge-dws'  :
                          row.device_type === 'desktop' ? 'rh-badge-archived' :
                          'rh-badge-overdue'
                        }`} style={{ fontSize: '0.72rem', textTransform: 'capitalize' }}>
                          {row.device_type}
                        </span>
                      </td>

                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem', color: 'var(--rh-text-muted)' }}>
                        {row.browser_name}
                      </td>

                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem', color: 'var(--rh-text-muted)' }}>
                        {row.os_name}
                      </td>

                      <td style={{ textAlign: 'center', fontSize: '0.85rem', color: isLowSample ? 'var(--rh-text-muted)' : undefined }}>
                        {row.total}
                        {isLowSample && (
                          <span title="Low sample size" style={{ marginLeft: '2px', fontSize: '0.7rem', opacity: 0.6 }}>⚠</span>
                        )}
                      </td>

                      <td style={{ textAlign: 'center' }}>
                        <span className="rh-badge rh-badge-live" style={{ minWidth: '28px', textAlign: 'center' }}>
                          {row.live}
                        </span>
                      </td>

                      <td style={{ textAlign: 'center' }}>
                        {row.deleted > 0
                          ? <span className="rh-badge rh-badge-deleted" style={{ minWidth: '28px', textAlign: 'center' }}>{row.deleted}</span>
                          : <span style={{ color: 'var(--rh-text-muted)' }}>—</span>}
                      </td>

                      <td style={{ textAlign: 'center' }}>
                        {row.instant_deleted > 0
                          ? <span className="rh-badge rh-badge-dws" style={{ minWidth: '28px', textAlign: 'center' }}>{row.instant_deleted}</span>
                          : <span style={{ color: 'var(--rh-text-muted)' }}>—</span>}
                      </td>

                      <td style={{ textAlign: 'center', padding: '6px 4px' }}>
                        <RateCell rate={row.deletion_rate} />
                      </td>

                      <td style={{ textAlign: 'center', padding: '6px 4px' }}>
                        <RateCell rate={row.instant_deletion_rate} />
                      </td>

                      <td style={{ textAlign: 'center', color: 'var(--rh-text-muted)', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {row.last_submission_at || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && !error && (
        <div style={{ marginTop: '8px' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--rh-text-muted)', margin: '0 0 4px' }}>
            {sorted.length} bucket{sorted.length !== 1 ? 's' : ''} shown.{' '}
            Del. Rate = (Deleted + Inst. Del.) ÷ Total × 100.{' '}
            Rows highlighted in yellow have fewer than 5 submissions — rates may not be representative.{' '}
            Device data reflects the browser used when interacting with this dashboard.
          </p>
          <p style={{ fontSize: '0.75rem', color: 'var(--rh-text-muted)', margin: 0 }}>
            <strong>Note:</strong> Submissions made before device detection was introduced are excluded from this report. Only submissions where browser/device data was successfully captured are shown.
          </p>
        </div>
      )}
    </div>
  );
}
